import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import {
  getRole14ValidatingControls,
  isRole14CalculatedControl,
  isRole14Empty,
  pickRole14Schema,
  resolveRole14ColumnSuffixes,
  Role14Schema,
  Role14SchemaControl,
} from './role14-sopl-by-nature.rules';
import { SHEET_KEYS } from '../../../models/sheet-keys';

/**
 * Pure validator for Role 14 (Statement of Profit or Loss — By Nature).
 *
 * Mirrors the editor's role14-320000-statement-of-profit-or-loss-by-nature.component.ts
 * sheet-status surface. The component publishes its status via exactly ONE
 * site:
 *
 *   this.stateService.markSheetInitializedWithStatus(
 *     'statementOfProfitOrLossByNature', false
 *   );
 *
 * — i.e. `hasErrors` is hard-coded to `false` at init and never re-published.
 * There is no `evaluateSheetStatus()` and no downstream `updateSheetLevelStatus`
 * call. The component's `calculateTotals()` and `validateAgainstApiData()`
 * flows produce row-level UI badges (`Valid` / `Valid*` / `Edited` / `Failed`)
 * but those map exclusively to the per-row template, not to the persisted
 * `SheetLevelStatusData`.
 *
 * Both schemas (MFRS, MPERS) declare ZERO `required: true` fields and ZERO
 * `validators` arrays today; the component's `buildForm()` attaches no
 * Angular validators at all (every `new FormControl(defaultValue, validators)`
 * is created with an empty `validators: ValidatorFn[] = []`). The pure path
 * therefore returns `hasErrors: false` deterministically — matching the
 * editor's published value bit-for-bit.
 *
 * ---- LB-001 fix-guard (latent-bug pattern) ------------------------------
 *
 * Even though no role 14 control trips the LB-001 pattern today, the
 * validator carries the same dual-source required check (`control.required ===
 * true` OR unconditional `validators[{ name: 'required' }]`) so any future
 * schema edit that adds a required field gets picked up automatically. This
 * mirrors the same guard on roles 3, 9, 11, 12, 13, 21 and 22. Calculated rows are skipped (the editor
 * never marks them required even after the calc engine runs).
 *
 * ---- Calculation qname mismatch (preserved, NOT fixed here) --------------
 *
 * The component's `calculateTotals()`, `autoFillDemo()`, and
 * `registerCalculatedKeys()` all reference `ssmt:RevenueByNature`,
 * `ssmt:ProfitFromOperationsByNature`, etc., but BOTH schemas declare these
 * controls with `ifrs-full:` (MFRS) or `ifrs-smes:` (MPERS) prefixes. The
 * form built from the schema therefore contains keys like
 * `ifrs-full:RevenueByNatureCurrent`, while `getFieldValue('ssmt:RevenueByNature',
 * 'Current')` reads `null` and `setValue('ssmt:ProfitFromOperationsByNatureCurrent',
 * ...)` no-ops because that control doesn't exist. Every calculated total in
 * the by-nature SOPL is silently broken — they all evaluate to 0.
 *
 * This is preserved (not fixed) in this lift because:
 *   1. Fixing it would suddenly populate calculated rows that previously
 *      stayed at 0, changing user-facing behaviour and potentially flagging
 *      conversions that "passed" before. Needs PO sign-off.
 *   2. The bug does NOT influence the published `SheetLevelStatusData`
 *      (calculations don't drive `hasErrors` for this sheet) so parity holds.
 *
 * P.O. decision (2026-10): role 14 (Statement of Profit or Loss by Nature) is not
 * supported for the foreseeable future, so this is deliberately left unfixed. If
 * the sheet is ever revived the fix is a prefix swap, not a rename: every local
 * name the calculation uses resolves as `ifrs-full:<name>` (MFRS) and
 * `ifrs-smes:<name>` (MPERS) in the two schemas — give `calculateTotals_320000` the
 * `xbrlPrefix` parameter its siblings take, and fix `autoFillDemo()` and
 * `registerCalculatedKeys()` in the same change. The four totals would then start
 * showing a figure instead of 0, which is the behaviour the spec already describes.
 *
 * ---- Cross-sheet posture ------------------------------------------------
 *
 * Per `SheetDependencyService`, role 14 invalidates when role 0 (central
 * config hub) or role 2 (`scopeOfFiling`) changes. The component's
 * `checkConsolidatedStatus()` reads `scopeOfFiling` for column-scenario
 * gating; `crossRefService.isMFRS()` reads `filingPrerequisites` (via
 * `accountingStandard`) for schema selection. From the validator's
 * perspective both only influence column visibility, which today is moot
 * since no validation rule consumes per-column data. The validator still
 * reads `context.crossSheetData` defensively and tolerates an absent slice
 * so the orchestrator can pass `{}` or omit keys without breaking parity —
 * mirroring the Tier 4 task contract.
 */

interface SchemaValidator {
  name?: string;
  conditionalOn?: unknown;
}

interface SchemaControlForRequired {
  qname: string;
  required?: boolean;
  validators?: SchemaValidator[];
}

/**
 * LB-001 fix-guard: a control is statically required when EITHER
 * `required === true` OR the validators array contains an unconditional
 * `{ name: 'required' }` entry. Mirrors the role-6 post-fix check so any
 * future schema edit to role 14 (none exist today) gets picked up
 * automatically.
 */
function hasUnconditionalRequiredViaValidators(
  control: SchemaControlForRequired
): boolean {
  return (
    control.validators?.some(
      (validator) => validator?.name === 'required' && validator?.conditionalOn === undefined
    ) ?? false
  );
}

function isStaticallyRequired(control: Role14SchemaControl): boolean {
  return (
    control.required === true ||
    hasUnconditionalRequiredViaValidators(control as SchemaControlForRequired)
  );
}

/**
 * Read a sheet's payload from the cross-sheet bundle, tolerating absence.
 * Used defensively — role 14's current validation logic does not consume
 * any cross-sheet slice, but the lookup exists so a future
 * `requiredIfCrossSheet` rule can plug in without re-threading the
 * interface. The orchestrator may pass an empty Map or omit the key; both
 * cases resolve to undefined here.
 */
function readCrossSheetSlice(
  context: SheetValidationContext,
  sheet: string
): Record<string, any> | undefined {
  const bundle = context.crossSheetData;
  if (!bundle) return undefined;
  return bundle.get(sheet as Parameters<typeof bundle.get>[0]) as
    | Record<string, any>
    | undefined;
}

/**
 * Build the flat form-control keys for a row across the visible column
 * suffixes. Mirrors the component's buildForm() for `dualColumn` rows:
 *   {qname}{GroupCurrent|GroupPrevious|CompanyCurrent|CompanyPrevious}
 *   or {qname}{Current|Previous}
 * For non-`dualColumn` rows (none in the current schema) the flat key is
 * just the qname — the same fallback the component would use if the schema
 * introduced one.
 */
function buildRowColumnKeys(
  control: Role14SchemaControl,
  suffixes: ReadonlyArray<string>
): string[] {
  if (control.ui?.dualColumn) {
    return suffixes.map((suffix) => `${control.qname}${suffix}`);
  }
  return [control.qname];
}

/**
 * Mirror of the editor's row-level "required field with no value -> Failed"
 * branch. Flagged only when ALL visible columns are empty — matches the
 * `hasValue = false` accumulator across the loop in `getRowValidationStatus()`.
 */
function checkRequiredRowErrors(
  schema: Role14Schema,
  sheetData: Record<string, any>,
  suffixes: ReadonlyArray<string>
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const control of getRole14ValidatingControls(schema)) {
    if (!isStaticallyRequired(control)) continue;
    if (isRole14CalculatedControl(control)) continue;

    const keys = buildRowColumnKeys(control, suffixes);
    const anyFilled = keys.some((key) => !isRole14Empty(sheetData[key]));
    if (!anyFilled) {
      errors[control.qname] = `${control.qname} is required`;
    }
  }

  return errors;
}

export const role14SoplByNatureValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.statementOfProfitOrLossByNature,
  // Tier 4 per phase-2 plan: alternative SOPL layout with row-sum
  // calculations and cross-sheet reads (scopeOfFiling for column scenario,
  // filingPrerequisites for schema variant). The calculations are
  // side-effect-only today (LB-014 — calculated rows compute to 0 due to
  // qname mismatch) and don't drive `hasErrors`, but the tier classification
  // stays at 4 to surface the dual-path parity check during dev — any future
  // fix to LB-014 plus a schema edit that adds equality / requiredIf rules
  // would plug in here without re-tiering.
  tier: 4,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    // Defensive cross-sheet reads — tolerated absent. Role 14 does not
    // consume these today; the lookups are present so future schema edits
    // (e.g. a `requiredIf scopeOfFiling.X equals Y` rule, or a cross-sheet
    // equality against role 12 / 19) plug in without re-threading the
    // interface. Silenced "unused" signal documents the intent for readers.
    const _scopeOfFiling = readCrossSheetSlice(context, SHEET_KEYS.scopeOfFiling);
    const _filingPrerequisites = readCrossSheetSlice(
      context,
      SHEET_KEYS.filingPrerequisites
    );
    void _filingPrerequisites;

    const schema = pickRole14Schema(context.accountingStandard);
    const suffixes = resolveRole14ColumnSuffixes(_scopeOfFiling);

    const errors: Record<string, string> = {
      ...checkRequiredRowErrors(schema, sheetData, suffixes),
    };

    return {
      hasErrors: Object.keys(errors).length > 0,
      // Developer Note (TODO): stub — `hasSuggestedChanges` is hard-coded false,
      // so this sheet's dropdown "*" can never light. When released as a live
      // monetary sheet, mirror role13: (1) recompute via
      // CalculationService.calculateTotals_<id> + hasCalculatedFieldMismatch(
      // context.extractedBaseline ?? sheetData, recomputed); (2) add its sheet key
      // to PURE_VERDICT_SHEETS; (3) call evaluateSheetStatus() in the component's
      // runAllValidations(); (4) add its sheet key to CALC_DIFF_SHEETS in
      // suggestion-capability.ts, or the "(*) Optional: Suggestions to refine
      // data" legend will stay hidden on this sheet even once its "*" can light.
      hasSuggestedChanges: false,
      errors,
    };
  },
};
